import { Brick, Predicate, Query, registerBrick, WorkflowObjectState } from 'olympe';
import { executeLambda } from '../../node/BackendData/LambdaExecutor';
import { getLogger } from '@olympeio/core';

import BCP from './DM_BCP.js';
import { BCP_WORKFLOW_STATES, ADMIN_FIRST_ACCEPTANCE } from './BcpWorkflowAuthorizations';
import { generateEmailContents, parseNameEmail } from './weeklyEmailTemplate.js';
import { sleep } from "../utils/CommonUtil.js"

export default class BCPWeeklyNotification extends Brick {
    /**
   * @override
   * @protected
   * @param {!BrickContext} $
   * @param {*} sendEmail
   * @param {function()} forwardEvent
   */

    async update($, [sendEmail], [forwardEvent,setErrorFlow]) {
        const logger = getLogger('BCPWeeklyNotification');

        logger.info(`BCPWeeklyNotification - Started at ${new Date(Date.now()).toISOString()}`);

        // Calculate the date 7 days before today and reset its time.
        const today = new Date();
        today.setDate(today.getDate() - 7);
        today.setHours(0, 0, 0, 0);

        logger.info(`BCPWeeklyNotification - Filtering BCP requests pending since: ${today}`);

        const validStates = [
            BCP_WORKFLOW_STATES.ALT_VS_SUGG_BY_BP,
            BCP_WORKFLOW_STATES.ALT_VS_CHECK_BY_PM,
            BCP_WORKFLOW_STATES.CTAG_ACCEPTANCE,
            BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_1,
            BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_2,
        ];
        // Create a Predicate.or condition for the target states
        const statePredicate = Predicate.or(...validStates.map((state) => Predicate.equals(WorkflowObjectState.stateProp, state)));

        // Create a Predicate for the date filter (less than 7 days)
        const datePredicate = Predicate.smallerThan(WorkflowObjectState.dateTimeProp, today);

        // Combine the predicates using Predicate.and
        const combinedPredicate = Predicate.and(datePredicate, statePredicate);

        let query = Query.instancesOf(BCP)
                         .andReturn()
                         .follow(WorkflowObjectState.currentObjectRel.getInverse())
                         .filter(combinedPredicate)
                         .andReturn();
                         
        try {
            const queryResult = await query.execute();
            logger.info('BCPWeeklyNotification - Query result', queryResult.toArray());
            const identifiedUserforBcp = [];
            // Process each BCP object and map it to relevant users based on its state.
            queryResult.toArray().forEach((element) => {
                const workflow = element[1].get(WorkflowObjectState.stateProp);
                const bcpObject = element[0];
                let emailToUser = [];

                // get user details by workflow state
                if (workflow === BCP_WORKFLOW_STATES.ALT_VS_CHECK_BY_PM) {
                    emailToUser.push(bcpObject.get(BCP.procurementManagerProp));
                } else if (workflow === BCP_WORKFLOW_STATES.ALT_VS_SUGG_BY_BP) {
                    emailToUser.push(bcpObject.get(BCP.businessPartnerProp));
                } else if (workflow === BCP_WORKFLOW_STATES.CTAG_ACCEPTANCE) {
                    emailToUser.push(bcpObject.get(BCP.technicalApplicationGroupProp));
                } else if (workflow === BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_1) {
                    emailToUser.push(bcpObject.get(BCP.qaInFactoryProp), bcpObject.get(BCP.qualityAssuranceProp));
                } else if (workflow === BCP_WORKFLOW_STATES.QA_FACTORY_ACCEPTANCE_2) {
                    const isAdminAcceptQA1 = bcpObject.get(BCP.adminFirstQualityAcceptanceProp);
                    if (isAdminAcceptQA1) {
                        emailToUser.push(isAdminAcceptQA1 === ADMIN_FIRST_ACCEPTANCE.ACCEPT_AS_SQA ? bcpObject.get(BCP.qaInFactoryProp) : bcpObject.get(BCP.qualityAssuranceProp));
                    } else {
                        const QA1User = element?.[1]?.get(WorkflowObjectState.assigneeProp)?.toLowerCase();
                        const qaInFactoryEmail = bcpObject.get(BCP.qaInFactoryProp)?.split(';')[1]?.toLowerCase();
                        emailToUser.push(QA1User && (QA1User === qaInFactoryEmail) ? bcpObject.get(BCP.qualityAssuranceProp) : bcpObject.get(BCP.qaInFactoryProp));
                    }
                }

                // Avoid duplicate emails
                emailToUser = [...new Set(emailToUser.map(email => email?.toLowerCase()))];
                identifiedUserforBcp.push({
                    bcpObjet: bcpObject,
                    emailto: emailToUser,
                });
            });

            // Group BCP objects by email addresses.
            const groupByEmail = (data) => {
                const emailMap = new Map();
                data.forEach(({ bcpObjet, emailto }) => {
                    emailto.forEach((email) => {
                        if (!emailMap.has(email)) {
                            emailMap.set(email, []);
                        }
                        emailMap.get(email).push(bcpObjet);
                    });
                });
                return Array.from(emailMap, ([mailto, bcpobjects]) => ({
                    mailto,
                    bcpobjects,
                }));
            };

            const groupedByEmail = groupByEmail(identifiedUserforBcp);
            logger.info('grouped by email', groupedByEmail);

            // Send emails asynchronously
            const emailPromises = groupedByEmail.map(async (emailData, index) => {
                logger.info("BCPWeeklyNotification - Before sending emails", index)
                try {
                    const emailContent = await generateEmailContents(emailData, $);
                    //logger.info(":: remainder: Normal weekly email ::", (index + 1) % 100)
                    if ((index + 1) % 100 === 0) {
                        await sleep(2000);
                    }
                    await executeLambda($, sendEmail, [parseNameEmail(emailData.mailto).email, 'BCP request - Weekly notification', emailContent]);
                    logger.info(`BCPWeeklyNotification - Email sent to ${parseNameEmail(emailData.mailto).email}`);
                } catch (error) {
                    logger.error(`BCPWeeklyNotification - Failed to send email to ${parseNameEmail(emailData.mailto).email}:`, error);
                    setErrorFlow(error)
                }
            });

            await Promise.all(emailPromises);
            logger.info('BCPWeeklyNotification - All weekly emails processed successfully.');
        } catch (error) {
            logger.error('Error executing query:', error);
            setErrorFlow(error)
        }
        finally {
            logger.info(`BCPWeeklyNotification - Completed at ${new Date(Date.now()).toISOString()}`);

            forwardEvent();
        }
    }
}

registerBrick('01943ff3ceb9d7c0e414', BCPWeeklyNotification);


